import { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  Mail,
  Send,
  MailOpen,
  Image,
  MessageSquare,
  Store,
  Info,
  FolderTree,
  ScrollText,
  Sparkles,
  X,
  ExternalLink,
  LogOut,
  Settings,
  Search,
  ChevronsUpDown,
  ChevronUp,
  Bell,
  CircleDot,
  SearchCheck,
  BarChart3,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { logout } from "@/features/auth/authSlice";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const navSections = [
  {
    label: "Pazarlama",
    items: [
      { to: "/newsletter", label: "Bülten Aboneleri", icon: Mail, testId: "nav-newsletter" },
      { to: "/campaigns", label: "Kampanyalar", icon: Send, testId: "nav-campaigns" },
    ],
  },
  {
    label: "Posta",
    items: [
      { to: "/mail-send", label: "Mail Gönder", icon: MailOpen, testId: "nav-mail-send" },
    ],
  },
  {
    label: "Web Sitesi",
    items: [
      { to: "/hero", label: "Hero Tanımları", icon: Image, testId: "nav-hero" },
      { to: "/about", label: "Hakkımızda Sayfası", icon: Info, testId: "nav-about" },
      { to: "/categories", label: "Kategori Tanımları", icon: FolderTree, testId: "nav-categories" },
      { to: "/policies", label: "Politikalar", icon: ScrollText, testId: "nav-policies" },
    ],
  },
  {
    label: "İletişim",
    items: [
      { to: "/messages", label: "Mesajlar", icon: MessageSquare, testId: "nav-messages" },
    ],
  },
  {
    label: "İşletme",
    items: [
      { to: "/branches", label: "Şube Tanımları", icon: Store, testId: "nav-branches" },
    ],
  },
  {
    label: "Analitik",
    items: [
      { to: "/guests", label: "Misafirler", icon: BarChart3, testId: "nav-guests" },
    ],
  },
  {
    label: "Ayarlar",
    items: [
      { to: "/seo", label: "SEO Ayarları", icon: SearchCheck, testId: "nav-seo" },
    ],
  },
];

const SITE_URL = "https://www.altingross.com";

export default function Sidebar({ mobileOpen, onClose }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const user = useSelector((s) => s.auth.user);
  const authCtx = useAuth();
  const [search, setSearch] = useState("");

  const handleLogout = async () => {
    await authCtx.logout();
    dispatch(logout());
    toast.success("Çıkış yapıldı");
    navigate("/login");
  };

  // Filter nav by search
  const filteredSections = navSections
    .map((section) => ({
      ...section,
      items: section.items.filter((it) =>
        it.label.toLowerCase().includes(search.toLowerCase())
      ),
    }))
    .filter((s) => s.items.length > 0);

  return (
    <>
      {/* Mobile overlay */}
      {mobileOpen && (
        <div
          className="fixed inset-0 z-30 bg-slate-900/40 backdrop-blur-sm lg:hidden"
          onClick={onClose}
          data-testid="sidebar-overlay"
        />
      )}

      <aside
        data-testid="sidebar"
        className={cn(
          "fixed top-0 left-0 z-40 h-full w-64 bg-white border-r border-slate-200/80 flex flex-col transition-transform duration-300",
          mobileOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
        )}
      >
        {/* Brand */}
        <div className="h-16 flex items-center justify-between px-4 border-b border-slate-100">
          <div className="flex items-center gap-2.5">
            <div className="relative">
              <img
                src="/images.png"
                alt="Altıngross Logo"
                className="h-10 w-10 object-cover rounded-xl shadow-md"
              />
              <div className="absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full bg-emerald-500 border-2 border-white" />
            </div>
            <div className="leading-tight">
              <div className="font-bold text-[15px] text-slate-900 tracking-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>
                Altıngross
              </div>
              <div className="text-[9px] uppercase tracking-[0.18em] text-slate-400 font-semibold">
                Admin v1.0
              </div>
            </div>
          </div>

          {/* Workspace switcher (decorative) + close on mobile */}
          <div className="flex items-center gap-1">
            <button
              className="hidden lg:flex h-7 w-7 items-center justify-center rounded-md hover:bg-slate-100 text-slate-400 transition-colors"
              data-testid="sidebar-workspace-switcher"
              title="Çalışma alanı"
            >
              <ChevronsUpDown className="h-3.5 w-3.5" />
            </button>
            <button
              onClick={onClose}
              className="lg:hidden h-7 w-7 flex items-center justify-center rounded-md hover:bg-slate-100"
              data-testid="sidebar-close-button"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Search */}
        <div className="px-3 pt-3 pb-2">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Menüde ara..."
              data-testid="sidebar-search-input"
              className="w-full pl-8 pr-12 h-8 bg-slate-50 border border-slate-200/80 rounded-md text-[13px] text-slate-700 placeholder:text-slate-400 focus:outline-none focus:bg-white focus:border-blue-400 focus:ring-2 focus:ring-blue-50 transition-all"
            />
            <kbd className="absolute right-2 top-1/2 -translate-y-1/2 hidden sm:inline-flex items-center px-1.5 h-5 rounded text-[10px] font-mono font-semibold bg-white border border-slate-200 text-slate-400">
              ⌘K
            </kbd>
          </div>
        </div>

        {/* Nav */}
        <nav className="flex-1 px-3 py-2 overflow-y-auto" data-testid="sidebar-nav">
          {filteredSections.length === 0 ? (
            <div className="px-3 py-8 text-center text-xs text-slate-400">
              Sonuç bulunamadı
            </div>
          ) : (
            filteredSections.map((section, sIdx) => (
              <div key={section.label} className={cn("space-y-0.5", sIdx > 0 && "mt-5")}>
                <div className="px-2.5 mb-1.5 flex items-center gap-2">
                  <span className="text-[10px] uppercase tracking-[0.15em] text-slate-400 font-bold">
                    {section.label}
                  </span>
                  <div className="flex-1 h-px bg-slate-100" />
                </div>
                {section.items.map((item) => (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    data-testid={item.testId}
                    onClick={onClose}
                    className={({ isActive }) =>
                      cn(
                        "group relative flex items-center gap-2.5 px-2.5 py-2 rounded-md text-[13px] font-medium transition-all",
                        isActive
                          ? "bg-blue-600 text-white shadow-sm shadow-blue-200"
                          : "text-slate-600 hover:bg-slate-100/80 hover:text-slate-900"
                      )
                    }
                  >
                    {({ isActive }) => (
                      <>
                        {isActive && (
                          <span className="absolute -left-3 top-1/2 -translate-y-1/2 h-5 w-1 bg-blue-600 rounded-r-full" />
                        )}
                        <item.icon
                          className={cn(
                            "h-4 w-4 transition-colors shrink-0",
                            isActive ? "text-white" : "text-slate-400 group-hover:text-blue-600"
                          )}
                          strokeWidth={isActive ? 2.5 : 2}
                        />
                        <span className="flex-1 truncate">{item.label}</span>
                        {item.badge && (
                          <span className={cn(
                            "px-1.5 h-4 inline-flex items-center rounded text-[9px] font-bold uppercase tracking-wider",
                            isActive
                              ? "bg-white/20 text-white"
                              : "bg-blue-600 text-white"
                          )}>
                            {item.badge}
                          </span>
                        )}
                        {item.count !== undefined && item.count > 0 && (
                          <span className={cn(
                            "ml-auto inline-flex items-center justify-center h-5 min-w-5 px-1.5 rounded-md text-[10px] font-bold",
                            isActive
                              ? "bg-white/20 text-white"
                              : "bg-slate-100 text-slate-600 group-hover:bg-blue-50 group-hover:text-blue-700"
                          )}>
                            {item.count}
                          </span>
                        )}
                      </>
                    )}
                  </NavLink>
                ))}
              </div>
            ))
          )}
        </nav>

        {/* System status */}
        <div className="px-3 pt-2 pb-2 border-t border-slate-100">
          <div className="flex items-center gap-2 px-2.5 py-1.5 rounded-md bg-emerald-50/60">
            <CircleDot className="h-3 w-3 text-emerald-500 fill-emerald-500" />
            <span className="text-[11px] font-medium text-emerald-800">Tüm sistemler aktif</span>
          </div>
        </div>

        {/* Footer: actions + user */}
        <div className="px-3 py-3 border-t border-slate-100 space-y-2">
          <a
            href={SITE_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="sidebar-visit-site"
            className="flex items-center justify-center gap-2 w-full h-9 rounded-md bg-blue-600 hover:bg-blue-700 text-white text-[13px] font-semibold transition-colors shadow-sm shadow-blue-200"
          >
            <ExternalLink className="h-3.5 w-3.5" />
            Siteye Git
          </a>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                data-testid="sidebar-user-card"
                className="w-full flex items-center gap-2.5 px-2 py-1.5 rounded-md hover:bg-slate-100 transition-colors text-left group"
              >
                <div className="relative shrink-0">
                  <Avatar className="h-9 w-9 ring-2 ring-white shadow-sm">
                    <AvatarImage src={user?.avatar} alt={user?.name || "Kullanıcı"} />
                    <AvatarFallback className="bg-blue-100 text-blue-700 text-xs font-bold">
                      {user?.name?.[0] || "D"}
                    </AvatarFallback>
                  </Avatar>
                  <span className="absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full bg-emerald-500 border-2 border-white" />
                </div>
                <div className="min-w-0 flex-1 leading-tight">
                  <div className="text-[13px] font-semibold text-slate-800 truncate">
                    {user?.name || "Kullanıcı"}
                  </div>
                </div>
                <ChevronUp className="h-3.5 w-3.5 text-slate-400 group-hover:text-slate-600 shrink-0" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" side="top" className="w-60" sideOffset={6}>
              <DropdownMenuLabel className="font-normal py-2.5">
                <div className="flex items-center gap-2.5">
                  <Avatar className="h-9 w-9">
                    <AvatarImage src={user?.avatar} alt={user?.name} />
                    <AvatarFallback className="bg-blue-100 text-blue-700 text-xs font-bold">
                      {user?.name?.[0] || "D"}
                    </AvatarFallback>
                  </Avatar>
                  <div className="min-w-0 leading-tight">
                    <div className="text-sm font-semibold text-slate-900 truncate">{user?.name || "—"}</div>
                    <div className="text-[11px] text-slate-500 truncate">{user?.email || "—"}</div>
                  </div>
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => navigate("/")} data-testid="sidebar-user-profile">
                <Settings className="h-3.5 w-3.5 mr-2" /> Hesap Ayarları
              </DropdownMenuItem>
              <DropdownMenuItem data-testid="sidebar-user-notifications">
                <Bell className="h-3.5 w-3.5 mr-2" /> Bildirimler
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={handleLogout}
                className="text-red-600 focus:bg-red-50 focus:text-red-700"
                data-testid="sidebar-user-logout"
              >
                <LogOut className="h-3.5 w-3.5 mr-2" /> Çıkış Yap
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </aside>
    </>
  );
}
